// The Apopilo offer composer's fields (Figma eWr6sqSGfDufrRNUMjbtK4 `Web / Apopilo / Composer modal` 2773:1282) and their
// request body. Pure: the modal keeps an `OfferDraft` and turns it into the create / update body here.
import type { ApopiloInput, ApopiloKind, EventLocation, PostItemInput, PostV2 } from "../../api/content";

export interface OfferPhoto { fileId: string; url: string }

export interface OfferDraft {
  kind: ApopiloKind;
  title: string;
  description: string;
  photos: OfferPhoto[];
  /** YYYY-MM-DD; empty = not picked. */
  startsOn: string;
  endsOn: string;
  place: EventLocation | null;
}

export type OfferField = "title" | "dates";

/** Tutor: EUR a day, paid through IEUP (spec 3.1). */
export const TUTOR_DAY_EUROS = 20;
/** One offer lasts at most this many days (Tutor) or nights (Apopilo): room-stc MaxUnits. */
export const MAX_UNITS = 90;

export function newOfferDraft(): OfferDraft {
  return { kind: "TUTOR", title: "", description: "", photos: [], startsOn: "", endsOn: "", place: null };
}

/** An edit's fields from the stored post. */
export function offerDraftOf(p: PostV2): OfferDraft {
  const a = p.apopilo;
  return {
    kind: a?.kind ?? "TUTOR",
    title: p.title ?? "",
    description: p.description ?? p.caption ?? "",
    photos: p.items.filter((it) => it.fileId != null && it.type === "PHOTO").map((it) => ({ fileId: it.fileId!, url: it.photoUrl ?? it.thumbnailUrl ?? "" })),
    startsOn: a?.startsOn ?? "",
    endsOn: a?.endsOn ?? "",
    place: a?.place ?? null,
  };
}

/** Days since the epoch of a YYYY-MM-DD key; null when it is not a real date. */
function dayNumber(key: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const back = new Date(ms).toISOString().slice(0, 10);
  return back === key ? ms / 86_400_000 : null;
}

/** Today in the browser's zone as YYYY-MM-DD. */
export function todayKey(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Tutor counts days (12-15 Jun = 4), Apopilo counts nights (1-3 Jul = 2); null while the dates are not both real. */
export function offerUnits(d: Pick<OfferDraft, "kind" | "startsOn" | "endsOn">): number | null {
  const s = dayNumber(d.startsOn);
  const e = dayNumber(d.endsOn);
  if (s == null || e == null) return null;
  return d.kind === "TUTOR" ? e - s + 1 : e - s;
}

/** Tutor's total in EUR (20 a day); null for Apopilo (no money) or while the dates are not valid. */
export function offerTotalEuros(d: Pick<OfferDraft, "kind" | "startsOn" | "endsOn">): number | null {
  const u = offerUnits(d);
  return d.kind === "TUTOR" && u != null && u >= 1 ? u * TUTOR_DAY_EUROS : null;
}

/** Same rule as room-stc ValidDates: starts after today, ends no earlier, 1..90 units. */
export function datesValid(d: Pick<OfferDraft, "kind" | "startsOn" | "endsOn">, today: string): boolean {
  const s = dayNumber(d.startsOn);
  const e = dayNumber(d.endsOn);
  const t = dayNumber(today);
  if (s == null || e == null || t == null) return false;
  if (s <= t || e < s) return false;
  const u = offerUnits(d)!;
  return u >= 1 && u <= MAX_UNITS;
}

/** Fields that cannot be sent as they are; the composer shows each one's reason under it after a tap on Post. */
export function offerErrors(d: OfferDraft, today: string): Partial<Record<OfferField, true>> {
  const out: Partial<Record<OfferField, true>> = {};
  if (d.title.trim() === "") out.title = true;
  if (!datesValid(d, today)) out.dates = true;
  return out;
}

const orUndef = (s: string): string | undefined => (s.trim() === "" ? undefined : s.trim());

/** The create / update body (call only when `offerErrors` is empty). */
export function toOfferInput(d: OfferDraft): { type: "APOPILO"; title: string; description: string | undefined; items: PostItemInput[]; apopilo: ApopiloInput } {
  const name = d.place?.placeName?.trim();
  return {
    type: "APOPILO",
    title: d.title.trim(),
    description: orUndef(d.description),
    items: d.photos.map((p) => ({ fileId: p.fileId })),
    apopilo: {
      kind: d.kind, startsOn: d.startsOn, endsOn: d.endsOn,
      place: d.place ? { lat: d.place.lat, lng: d.place.lng, placeName: name ? name : null } : null,
    },
  };
}
